import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  AlertColor,
  CircularProgress,
  Autocomplete,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import LoadingOverlay from '../components/LoadingOverlay';
import CompanyDocuments from '../components/CompanyDocuments';
import LogoUpload from '../components/LogoUpload';
import SignatureUpload from '../components/SignatureUpload';
import StyledDataGrid from '../components/StyledDataGrid';

const COUNTRY_CODES = [
  { code: '+1', name: 'USA/Canada' },
  { code: '+7', name: 'Russia/Kazakhstan' },
  { code: '+20', name: 'Egypt' },
  { code: '+27', name: 'South Africa' },
  { code: '+30', name: 'Greece' },
  { code: '+31', name: 'Netherlands' },
  { code: '+32', name: 'Belgium' },
  { code: '+33', name: 'France' },
  { code: '+34', name: 'Spain' },
  { code: '+39', name: 'Italy' },
  { code: '+40', name: 'Romania' },
  { code: '+41', name: 'Switzerland' },
  { code: '+43', name: 'Austria' },
  { code: '+44', name: 'UK' },
  { code: '+45', name: 'Denmark' },
  { code: '+46', name: 'Sweden' },
  { code: '+47', name: 'Norway' },
  { code: '+48', name: 'Poland' },
  { code: '+49', name: 'Germany' },
  { code: '+51', name: 'Peru' },
  { code: '+52', name: 'Mexico' },
  { code: '+53', name: 'Cuba' },
  { code: '+54', name: 'Argentina' },
  { code: '+55', name: 'Brazil' },
  { code: '+56', name: 'Chile' },
  { code: '+60', name: 'Malaysia' },
  { code: '+61', name: 'Australia' },
  { code: '+62', name: 'Indonesia' },
  { code: '+63', name: 'Philippines' },
  { code: '+64', name: 'New Zealand' },
  { code: '+65', name: 'Singapore' },
  { code: '+66', name: 'Thailand' },
  { code: '+81', name: 'Japan' },
  { code: '+82', name: 'South Korea' },
  { code: '+84', name: 'Vietnam' },
  { code: '+86', name: 'China' },
  { code: '+90', name: 'Turkey' },
  { code: '+91', name: 'India' },
  { code: '+92', name: 'Pakistan' },
  { code: '+93', name: 'Afghanistan' },
  { code: '+94', name: 'Sri Lanka' },
  { code: '+95', name: 'Myanmar' },
  { code: '+98', name: 'Iran' },
  { code: '+212', name: 'Morocco' },
  { code: '+213', name: 'Algeria' },
  { code: '+216', name: 'Tunisia' },
  { code: '+218', name: 'Libya' },
  { code: '+220', name: 'Gambia' },
  { code: '+221', name: 'Senegal' },
  { code: '+222', name: 'Mauritania' },
  { code: '+223', name: 'Mali' },
  { code: '+224', name: 'Guinea' },
  { code: '+225', name: 'Ivory Coast' },
  { code: '+226', name: 'Burkina Faso' },
  { code: '+227', name: 'Niger' },
  { code: '+228', name: 'Togo' },
  { code: '+229', name: 'Benin' },
  { code: '+230', name: 'Mauritius' },
  { code: '+231', name: 'Liberia' },
  { code: '+232', name: 'Sierra Leone' },
  { code: '+233', name: 'Ghana' },
  { code: '+234', name: 'Nigeria' },
  { code: '+235', name: 'Chad' },
  { code: '+236', name: 'Central African Republic' },
  { code: '+237', name: 'Cameroon' },
  { code: '+238', name: 'Cape Verde' },
  { code: '+239', name: 'Sao Tome and Principe' },
  { code: '+240', name: 'Equatorial Guinea' },
  { code: '+241', name: 'Gabon' },
  { code: '+242', name: 'Congo' },
  { code: '+243', name: 'DR Congo' },
  { code: '+244', name: 'Angola' },
  { code: '+245', name: 'Guinea-Bissau' },
  { code: '+246', name: 'British Indian Ocean Territory' },
  { code: '+248', name: 'Seychelles' },
  { code: '+249', name: 'Sudan' },
  { code: '+250', name: 'Rwanda' },
  { code: '+251', name: 'Ethiopia' },
  { code: '+252', name: 'Somalia' },
  { code: '+253', name: 'Djibouti' },
  { code: '+254', name: 'Kenya' },
  { code: '+255', name: 'Tanzania' },
  { code: '+256', name: 'Uganda' },
  { code: '+257', name: 'Burundi' },
  { code: '+258', name: 'Mozambique' },
  { code: '+260', name: 'Zambia' },
  { code: '+261', name: 'Madagascar' },
  { code: '+262', name: 'Reunion' },
  { code: '+263', name: 'Zimbabwe' },
  { code: '+264', name: 'Namibia' },
  { code: '+265', name: 'Malawi' },
  { code: '+266', name: 'Lesotho' },
  { code: '+267', name: 'Botswana' },
  { code: '+268', name: 'Swaziland' },
  { code: '+269', name: 'Comoros' },
  { code: '+290', name: 'Saint Helena' },
  { code: '+291', name: 'Eritrea' },
  { code: '+297', name: 'Aruba' },
  { code: '+298', name: 'Faroe Islands' },
  { code: '+299', name: 'Greenland' },
  { code: '+350', name: 'Gibraltar' },
  { code: '+351', name: 'Portugal' },
  { code: '+352', name: 'Luxembourg' },
  { code: '+353', name: 'Ireland' },
  { code: '+354', name: 'Iceland' },
  { code: '+355', name: 'Albania' },
  { code: '+356', name: 'Malta' },
  { code: '+357', name: 'Cyprus' },
  { code: '+358', name: 'Finland' },
  { code: '+359', name: 'Bulgaria' },
  { code: '+370', name: 'Lithuania' },
  { code: '+371', name: 'Latvia' },
  { code: '+372', name: 'Estonia' },
  { code: '+373', name: 'Moldova' },
  { code: '+374', name: 'Armenia' },
  { code: '+375', name: 'Belarus' },
  { code: '+376', name: 'Andorra' },
  { code: '+377', name: 'Monaco' },
  { code: '+378', name: 'San Marino' },
  { code: '+380', name: 'Ukraine' },
  { code: '+381', name: 'Serbia' },
  { code: '+382', name: 'Montenegro' },
  { code: '+383', name: 'Kosovo' },
  { code: '+385', name: 'Croatia' },
  { code: '+386', name: 'Slovenia' },
  { code: '+387', name: 'Bosnia and Herzegovina' },
  { code: '+389', name: 'North Macedonia' },
  { code: '+420', name: 'Czech Republic' },
  { code: '+421', name: 'Slovakia' },
  { code: '+423', name: 'Liechtenstein' },
  { code: '+500', name: 'Falkland Islands' },
  { code: '+501', name: 'Belize' },
  { code: '+502', name: 'Guatemala' },
  { code: '+503', name: 'El Salvador' },
  { code: '+504', name: 'Honduras' },
  { code: '+505', name: 'Nicaragua' },
  { code: '+506', name: 'Costa Rica' },
  { code: '+507', name: 'Panama' },
  { code: '+508', name: 'Saint Pierre and Miquelon' },
  { code: '+509', name: 'Haiti' },
  { code: '+590', name: 'Guadeloupe' },
  { code: '+591', name: 'Bolivia' },
  { code: '+592', name: 'Guyana' },
  { code: '+593', name: 'Ecuador' },
  { code: '+594', name: 'French Guiana' },
  { code: '+595', name: 'Paraguay' },
  { code: '+596', name: 'Martinique' },
  { code: '+597', name: 'Suriname' },
  { code: '+598', name: 'Uruguay' },
  { code: '+599', name: 'Netherlands Antilles' },
  { code: '+670', name: 'East Timor' },
  { code: '+672', name: 'Antarctica' },
  { code: '+673', name: 'Brunei' },
  { code: '+674', name: 'Nauru' },
  { code: '+675', name: 'Papua New Guinea' },
  { code: '+676', name: 'Tonga' },
  { code: '+677', name: 'Solomon Islands' },
  { code: '+678', name: 'Vanuatu' },
  { code: '+679', name: 'Fiji' },
  { code: '+680', name: 'Palau' },
  { code: '+681', name: 'Wallis and Futuna' },
  { code: '+682', name: 'Cook Islands' },
  { code: '+683', name: 'Niue' },
  { code: '+685', name: 'Samoa' },
  { code: '+686', name: 'Kiribati' },
  { code: '+687', name: 'New Caledonia' },
  { code: '+688', name: 'Tuvalu' },
  { code: '+689', name: 'French Polynesia' },
  { code: '+690', name: 'Tokelau' },
  { code: '+691', name: 'Micronesia' },
  { code: '+692', name: 'Marshall Islands' },
  { code: '+850', name: 'North Korea' },
  { code: '+852', name: 'Hong Kong' },
  { code: '+853', name: 'Macau' },
  { code: '+855', name: 'Cambodia' },
  { code: '+856', name: 'Laos' },
  { code: '+880', name: 'Bangladesh' },
  { code: '+886', name: 'Taiwan' },
  { code: '+960', name: 'Maldives' },
  { code: '+961', name: 'Lebanon' },
  { code: '+962', name: 'Jordan' },
  { code: '+963', name: 'Syria' },
  { code: '+964', name: 'Iraq' },
  { code: '+965', name: 'Kuwait' },
  { code: '+966', name: 'Saudi Arabia' },
  { code: '+967', name: 'Yemen' },
  { code: '+968', name: 'Oman' },
  { code: '+970', name: 'Palestine' },
  { code: '+971', name: 'UAE' },
  { code: '+972', name: 'Israel' },
  { code: '+973', name: 'Bahrain' },
  { code: '+974', name: 'Qatar' },
  { code: '+975', name: 'Bhutan' },
  { code: '+976', name: 'Mongolia' },
  { code: '+977', name: 'Nepal' },
  { code: '+992', name: 'Tajikistan' },
  { code: '+993', name: 'Turkmenistan' },
  { code: '+994', name: 'Azerbaijan' },
  { code: '+995', name: 'Georgia' },
  { code: '+996', name: 'Kyrgyzstan' },
  { code: '+998', name: 'Uzbekistan' }
];

const Companies = ({ access = 'none' }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedCompanyForDocs, setSelectedCompanyForDocs] = useState(null);
  const [pendingLogoBlob, setPendingLogoBlob] = useState<Blob | null>(null);
  const [pendingSignatureBlob, setPendingSignatureBlob] = useState<Blob | null>(null);
  const [pendingSignatureSettings, setPendingSignatureSettings] = useState<{ show_date: boolean; show_name: boolean }>({ show_date: true, show_name: true });
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    website: ''
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<any>(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_code: '',
    description: '',
    address: '',
    country_code: '+65',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    gst_number: '',
    vat_number: '',
    tax_rate: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_swift_code: '',
    standard_terms: '',
    signature_block: '',
    default_currency: 'SGD',
    date_format: 'DD/MM/YYYY',
    number_format: '1,234.56',
    timezone: 'Asia/Singapore'
  });

  const showNotification = (message: string, severity: AlertColor = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const canRead = access !== 'none';
  const canWrite = access === 'write' || access === 'admin';

  useEffect(() => {
    if (canRead) {
      fetchCompanies();
    }
  }, [canRead]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl('/api/companies'), {
        credentials: 'include'  // Include cookies for session auth
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      } else {
        throw new Error('Failed to fetch companies');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        company_name: company.company_name || '',
        company_code: company.company_code || '',
        description: company.description || '',
        address: company.address || '',
        country_code: company.country_code || '+65',
        phone: company.phone || '',
        email: company.email || '',
        website: company.website || '',
        logo_url: company.logo_url || '',
        gst_number: company.gst_number || '',
        vat_number: company.vat_number || '',
        tax_rate: company.tax_rate || '',
        bank_name: company.bank_name || '',
        bank_account_number: company.bank_account_number || '',
        bank_ifsc_code: company.bank_ifsc_code || '',
        bank_swift_code: company.bank_swift_code || '',
        standard_terms: company.standard_terms || '',
        signature_block: company.signature_block || '',
        default_currency: company.default_currency || 'SGD',
        date_format: company.date_format || 'DD/MM/YYYY',
        number_format: company.number_format || '1,234.56',
        timezone: company.timezone || 'Asia/Singapore'
      });
    } else {
      setEditingCompany(null);
      setFormData({
        company_name: '',
        company_code: '',
        description: '',
        address: '',
        country_code: '+65',
        phone: '',
        email: '',
        website: '',
        logo_url: '',
        gst_number: '',
        vat_number: '',
        tax_rate: '',
        bank_name: '',
        bank_account_number: '',
        bank_ifsc_code: '',
        bank_swift_code: '',
        standard_terms: '',
        signature_block: '',
        default_currency: 'SGD',
        date_format: 'DD/MM/YYYY',
        number_format: '1,234.56',
        timezone: 'Asia/Singapore'
      });
    }
    setActiveTab(0);
    setValidationErrors({ email: '', website: '' });
    setOpenDialog(true);
  };

  // Open documents-only dialog
  const handleOpenDocuments = (company) => {
    setSelectedCompanyForDocs(company);
    setDocumentsDialogOpen(true);
  };

  const handleCloseDocumentsDialog = () => {
    setDocumentsDialogOpen(false);
    setSelectedCompanyForDocs(null);
    // Refresh companies to update document count
    fetchCompanies();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCompany(null);
    setActiveTab(0);
    setPendingLogoBlob(null);
    setPendingSignatureBlob(null);
    setPendingSignatureSettings({ show_date: true, show_name: true });
    setValidationErrors({ email: '', website: '' });
    setFormData({
      company_name: '',
      company_code: '',
      description: '',
      address: '',
      country_code: '+65',
      phone: '',
      email: '',
      website: '',
      logo_url: '',
      gst_number: '',
      vat_number: '',
      tax_rate: '',
      bank_name: '',
      bank_account_number: '',
      bank_ifsc_code: '',
      bank_swift_code: '',
      standard_terms: '',
      signature_block: '',
      default_currency: 'SGD',
      date_format: 'DD/MM/YYYY',
      number_format: '1,234.56',
      timezone: 'Asia/Singapore'
    });
  };

  const validateEmail = (email) => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Invalid email format';
  };

  const validateUrl = (url) => {
    if (!url) return '';

    // Auto-prepend https:// if URL doesn't have a protocol
    let urlToValidate = url;
    const hasProtocol = url.toLowerCase().startsWith('http://') || url.toLowerCase().startsWith('https://');

    if (!hasProtocol) {
      // If URL contains a dot (like google.com, www.google.com, bimsector.com), prepend https://
      if (url.includes('.')) {
        urlToValidate = 'https://' + url;
      }
    }

    try {
      new URL(urlToValidate);
      return '';
    } catch {
      return 'Invalid URL format (e.g., https://example.com or example.com)';
    }
  };

  const handleInputChange = (field, value) => {
    // Auto-prepend https:// if website field doesn't have protocol
    let processedValue = value;
    if (field === 'website' && value) {
      const hasProtocol = value.toLowerCase().startsWith('http://') || value.toLowerCase().startsWith('https://');
      // If no protocol and contains a dot (domain name), prepend https://
      if (!hasProtocol && value.includes('.')) {
        processedValue = 'https://' + value;
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Validate specific fields
    if (field === 'email') {
      setValidationErrors(prev => ({ ...prev, email: validateEmail(processedValue) }));
    } else if (field === 'website') {
      setValidationErrors(prev => ({ ...prev, website: validateUrl(processedValue) }));
    }
  };

  const handleSubmit = async () => {
    if (saving) return;

    // Check for validation errors
    const hasErrors = Object.values(validationErrors).some(error => error !== '');
    if (hasErrors) {
      setError('Please fix validation errors before submitting');
      return;
    }

    try {
      setSaving(true);
      const url = editingCompany
        ? apiUrl(`/api/companies/${editingCompany.company_id}`)
        : apiUrl('/api/companies');

      const method = editingCompany ? 'PUT' : 'POST';

      // Clean up blob: URLs and normalize website URL before sending to backend
      const dataToSend = { ...formData };
      if (dataToSend.logo_url && dataToSend.logo_url.startsWith('blob:')) {
        dataToSend.logo_url = editingCompany ? editingCompany.logo_url || '' : '';
      }

      // Auto-prepend https:// to website if it doesn't have protocol
      if (dataToSend.website && dataToSend.website.trim()) {
        const hasProtocol = dataToSend.website.toLowerCase().startsWith('http://') || dataToSend.website.toLowerCase().startsWith('https://');
        // If no protocol and contains a dot (domain name), prepend https://
        if (!hasProtocol && dataToSend.website.includes('.')) {
          dataToSend.website = 'https://' + dataToSend.website;
        }
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        const savedCompany = await response.json();

        // If there's a pending logo blob, upload it (for both new and edit)
        const companyIdToUse = editingCompany ? editingCompany.company_id : savedCompany.company?.company_id;
        if (pendingLogoBlob && companyIdToUse) {
          try {
            const logoFormData = new FormData();
            logoFormData.append('file', pendingLogoBlob, 'logo.png');

            const logoResponse = await fetch(
              apiUrl(`/api/companies/${companyIdToUse}/upload-logo`),
              {
                method: 'POST',
                credentials: 'include',
                body: logoFormData
              }
            );

            if (!logoResponse.ok) {
              console.error('Failed to upload logo');
            }
          } catch (logoErr) {
            console.error('Error uploading logo:', logoErr);
          }
        }

        // If there's a pending signature blob, upload it (for both new and edit)
        if (pendingSignatureBlob && companyIdToUse) {
          try {
            const signatureFormData = new FormData();
            signatureFormData.append('file', pendingSignatureBlob, 'signature.png');

            const signatureResponse = await fetch(
              apiUrl(`/api/companies/${companyIdToUse}/upload-signature`),
              {
                method: 'POST',
                credentials: 'include',
                body: signatureFormData
              }
            );

            if (signatureResponse.ok) {
              // Update signature settings
              const signatureData = {
                ...await signatureResponse.json(),
                show_date: pendingSignatureSettings.show_date,
                show_name: pendingSignatureSettings.show_name
              };

              // Update company with signature settings
              await fetch(
                apiUrl(`/api/companies/${companyIdToUse}`),
                {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({
                    signature_block: JSON.stringify({
                      signature_image_url: signatureData.signature_url,
                      show_date: pendingSignatureSettings.show_date,
                      show_name: pendingSignatureSettings.show_name
                    })
                  })
                }
              );
            } else {
              console.error('Failed to upload signature');
            }
          } catch (signatureErr) {
            console.error('Error uploading signature:', signatureErr);
          }
        }

        await fetchCompanies();
        handleCloseDialog();
        showNotification(
          editingCompany
            ? 'Company updated successfully!'
            : 'Company created successfully!',
          'success'
        );
        // Notify App.tsx to refresh companies list
        window.dispatchEvent(new Event('companyUpdated'));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save company');
      }
    } catch (err) {
      setError(err.message);
      showNotification(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (deletingId || !companyToDelete) return;

    try {
      setDeletingId(companyToDelete.company_id);
      const response = await fetch(apiUrl(`/api/companies/${companyToDelete.company_id}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchCompanies();
        showNotification('Company deleted successfully!', 'success');
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
        // Notify App.tsx to refresh companies list in sidebar
        window.dispatchEvent(new Event('companyUpdated'));
      } else {
        const data = await response.json();
        showNotification(data.error || 'Failed to delete company', 'error');
      }
    } catch (err) {
      showNotification('Error deleting company: ' + err.message, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogoChange = async (logoUrl: string) => {
    setFormData(prev => ({ ...prev, logo_url: logoUrl }));
    await fetchCompanies();
    showNotification('Logo uploaded successfully!', 'success');
    window.dispatchEvent(new Event('companyUpdated'));
  };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'company_code',
      headerName: 'Company Code',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'company_name',
      headerName: 'Company Name',
      flex: 1.5,
      minWidth: 180
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || 'No description'}
        </Typography>
      )
    },
    {
      field: 'contact',
      headerName: 'Contact',
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      filterable: false,
      valueGetter: (value, row) => {
        // Return contact info for filtering
        const contact = [];
        if (row.email) contact.push(row.email);
        if (row.phone) contact.push(`${row.country_code} ${row.phone}`);
        return contact.join(' ');
      },
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
          {params.row.email && (
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              📧 {params.row.email}
            </Typography>
          )}
          {params.row.phone && (
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              📞 {params.row.country_code} {params.row.phone}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      valueGetter: (value, row) => {
        // Return status label for filtering to work
        return row.is_active ? 'Active' : 'Inactive';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'default'}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'document_count',
      headerName: 'Attachments',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          icon={<AttachFileIcon sx={{ fontSize: '1rem' }} />}
          label={params.row.document_count || 0}
          size="small"
          color={params.row.document_count > 0 ? 'primary' : 'default'}
          onClick={(e) => { e.stopPropagation(); handleOpenDocuments(params.row); }}
          sx={{
            cursor: 'pointer',
            fontWeight: 500,
            '&:hover': {
              bgcolor: params.row.document_count > 0 ? 'primary.dark' : 'grey.300'
            }
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canWrite && (
            <>
              <Tooltip title="Edit Company">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(params.row)}
                  disabled={!!deletingId}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Company">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(params.row)}
                  disabled={deletingId === params.row.company_id}
                  color="error"
                >
                  {deletingId === params.row.company_id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  if (!canRead) {
    return (
      <Box>
        <Alert severity="warning">
          You don't have permission to view companies.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <LoadingOverlay loading={loading} size={50} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Company
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Companies DataGrid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={companies}
          columns={columns}
          getRowId={(row) => row.company_id}
          loading={loading}
          paginationMode="client"
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: editingCompany ? 0 : 2, borderBottom: editingCompany ? 0 : 1, borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight={600}>
            {editingCompany ? 'Edit Company' : 'Add New Company'}
          </Typography>
        </DialogTitle>
        {editingCompany && (
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Company Details" />
            <Tab label="Documents" />
          </Tabs>
        )}
        <DialogContent sx={{ pt: 3, pb: 3, mt: editingCompany ? 0 : 2 }}>
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Company Name *"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  required
                  size="small"
                />
              <TextField
                label="Company Code *"
                value={formData.company_code}
                onChange={(e) => handleInputChange('company_code', e.target.value)}
                required
                disabled={!!editingCompany}
                size="small"
              />
            </Box>

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
              <Autocomplete
                value={COUNTRY_CODES.find(c => c.code === formData.country_code) || null}
                onChange={(event, newValue) => {
                  handleInputChange('country_code', newValue ? newValue.code : '+65');
                }}
                options={COUNTRY_CODES}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country Code"
                    size="small"
                    placeholder="Search country code..."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                size="small"
              />
              <TextField
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  handleInputChange('phone', value);
                }}
                placeholder="Enter numbers only"
                helperText="Numbers only, no spaces or symbols"
                size="small"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                size="small"
                error={!!validationErrors.email}
                helperText={validationErrors.email || 'example@company.com'}
              />
              <TextField
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                size="small"
                error={!!validationErrors.website}
                helperText={validationErrors.website || 'https://www.company.com'}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Company Logo
                </Typography>
                <LogoUpload
                  companyId={editingCompany?.company_id || null}
                  currentLogoUrl={formData.logo_url}
                  onLogoChange={handleLogoChange}
                  onUrlChange={(url) => handleInputChange('logo_url', url)}
                  onPendingImageChange={setPendingLogoBlob}
                  disabled={saving}
                />
              </Box>
              <TextField
                label="GST Number"
                value={formData.gst_number}
                onChange={(e) => handleInputChange('gst_number', e.target.value)}
                size="small"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="VAT Number"
                value={formData.vat_number}
                onChange={(e) => handleInputChange('vat_number', e.target.value)}
                placeholder="Enter VAT registration number"
                size="small"
              />
              <TextField
                label="Tax Rate (%)"
                type="number"
                value={formData.tax_rate}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                    handleInputChange('tax_rate', value);
                  }
                }}
                placeholder="e.g., 9"
                slotProps={{ htmlInput: { min: 0, max: 100, step: 0.01 } }}
                helperText="Enter tax rate as percentage (0-100)"
                size="small"
              />
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 1, mb: -1, fontWeight: 600, color: 'primary.main' }}>
              Bank Details
            </Typography>

            <TextField
              label="Bank Name"
              value={formData.bank_name}
              onChange={(e) => handleInputChange('bank_name', e.target.value)}
              fullWidth
              size="small"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Bank Account Number"
                value={formData.bank_account_number}
                onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                size="small"
              />
              <TextField
                label="IFSC Code"
                value={formData.bank_ifsc_code}
                onChange={(e) => handleInputChange('bank_ifsc_code', e.target.value)}
                size="small"
              />
            </Box>

            <TextField
              label="SWIFT Code"
              value={formData.bank_swift_code}
              onChange={(e) => handleInputChange('bank_swift_code', e.target.value)}
              fullWidth
              size="small"
            />

            <Typography variant="subtitle1" sx={{ mt: 1, mb: -1, fontWeight: 600, color: 'primary.main' }}>
              Terms & Conditions
            </Typography>

            <TextField
              label="Standard Terms"
              value={formData.standard_terms}
              onChange={(e) => handleInputChange('standard_terms', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Enter standard contract terms..."
              size="small"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Signature Block
              </Typography>
              <SignatureUpload
                companyId={editingCompany?.company_id || null}
                signatureBlock={formData.signature_block}
                onSignatureChange={(signatureData) => {
                  handleInputChange('signature_block', JSON.stringify(signatureData));
                  fetchCompanies();
                  showNotification('Signature updated successfully!', 'success');
                }}
                onPendingSignatureChange={(blob, settings) => {
                  setPendingSignatureBlob(blob);
                  setPendingSignatureSettings(settings);
                }}
                disabled={saving}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1.5, mt: 2, fontWeight: 600, color: 'text.primary' }}>
              Company Preferences
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                select
                label="Default Currency"
                value={formData.default_currency}
                onChange={(e) => handleInputChange('default_currency', e.target.value)}
                size="small"
              >
                <MenuItem value="SGD">SGD - Singapore Dollar</MenuItem>
                <MenuItem value="USD">USD - US Dollar</MenuItem>
                <MenuItem value="EUR">EUR - Euro</MenuItem>
                <MenuItem value="GBP">GBP - British Pound</MenuItem>
                <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                <MenuItem value="MYR">MYR - Malaysian Ringgit</MenuItem>
                <MenuItem value="AUD">AUD - Australian Dollar</MenuItem>
                <MenuItem value="JPY">JPY - Japanese Yen</MenuItem>
                <MenuItem value="CNY">CNY - Chinese Yuan</MenuItem>
              </TextField>

              <TextField
                select
                label="Date Format"
                value={formData.date_format}
                onChange={(e) => handleInputChange('date_format', e.target.value)}
                size="small"
              >
                <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                select
                label="Number Format"
                value={formData.number_format}
                onChange={(e) => handleInputChange('number_format', e.target.value)}
                size="small"
              >
                <MenuItem value="1,234.56">1,234.56 (Comma separator)</MenuItem>
                <MenuItem value="1.234,56">1.234,56 (Dot separator)</MenuItem>
                <MenuItem value="1 234.56">1 234.56 (Space separator)</MenuItem>
              </TextField>

              <TextField
                select
                label="Timezone"
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                size="small"
              >
                <MenuItem value="Asia/Singapore">Asia/Singapore (SGT)</MenuItem>
                <MenuItem value="Asia/Kolkata">Asia/Kolkata (IST)</MenuItem>
                <MenuItem value="America/New_York">America/New_York (EST)</MenuItem>
                <MenuItem value="Europe/London">Europe/London (GMT)</MenuItem>
                <MenuItem value="Australia/Sydney">Australia/Sydney (AEDT)</MenuItem>
                <MenuItem value="Asia/Tokyo">Asia/Tokyo (JST)</MenuItem>
              </TextField>
            </Box>
            </Box>
          )}
          {activeTab === 1 && editingCompany && (
            <CompanyDocuments companyId={editingCompany.company_id} canWrite={canWrite} />
          )}
        </DialogContent>
        {activeTab === 0 && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={handleCloseDialog} disabled={saving} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.company_name || !formData.company_code || saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? 'Saving...' : editingCompany ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        )}
        {activeTab === 1 && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Documents Only Dialog */}
      <Dialog
        open={documentsDialogOpen}
        onClose={handleCloseDocumentsDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {selectedCompanyForDocs?.company_name} - Documents
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, minHeight: 400 }}>
          {selectedCompanyForDocs && (
            <CompanyDocuments
              companyId={selectedCompanyForDocs.company_id}
              canWrite={canWrite}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseDocumentsDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete Company
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the following company?
          </Typography>
          {companyToDelete && (
            <Box sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.300'
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Company Code
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                {companyToDelete.company_code}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Company Name
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {companyToDelete.company_name}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            All groups associated with this company will become "Unassigned", but users will retain their group assignments and permissions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={deletingId}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deletingId}
            startIcon={deletingId ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingId ? 'Deleting...' : 'Delete Company'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;